"use client";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import Image from "next/legacy/image";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import Blockies from "react-blockies";
import Marquee from "react-fast-marquee";
import { useMediaQuery } from "usehooks-ts";
import { toHex } from "viem";
import { useAccount, useBalance, useDisconnect } from "wagmi";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";

import { useAddToHomescreenPrompt } from "../hooks/useAddToHomescreenPrompt";
import { useENS } from "../hooks/useENS";
import { Button } from "./Button";
import Menu, { FEATURED_IDS } from "./Menu";
import { PushNotifications } from "./PushNotifications";
import StaticTrack from "./StaticTrack";

function getRandomInt(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

const MenuDrawer = ({ today, current }: { today: bigint; current: bigint }) => {
  const router = useRouter();
  const isTrigger = useMediaQuery("(max-width: 1024px)");
  const [open, setOpen] = useState(false);
  const [granted, setGranted] = useState<
    "granted" | "denied" | "default" | "unsupported"
  >();
  const { openConnectModal } = useConnectModal();
  const { address, isConnected } = useAccount();
  const { data: ensData } = useENS({ address });
  const { disconnect } = useDisconnect();
  const { data: balanceData } = useBalance({ address, chainId: 8453 });
  const [prompt, promptToInstall] = useAddToHomescreenPrompt();

  const previous = [
    BigInt(today) - 2n,
    BigInt(today) - 1n,
    BigInt(today),
  ].filter((id) => id !== current);

  const register = async ({ address }: { address: string }) => {
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        navigator.serviceWorker.ready
          .then((registration) => {
            return registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: process.env.NEXT_PUBLIC_VAPID_PUBKEY,
            });
          })
          .then((subscription) => {
            return fetch("api/pushSubscription", {
              method: "POST",
              body: JSON.stringify({ subscription, address }),
            });
          })
          .then(() => {
            setGranted(permission);
          })
          .catch((e) => {
            alert(e);
          });
      }
    });
  };

  useEffect(() => {
    if (typeof Notification === "undefined") {
      setGranted("unsupported");
      return;
    }
    setGranted(Notification.permission);
  }, []);

  return (
    <Drawer
      open={open}
      shouldScaleBackground={false}
      onOpenChange={(_open) => setOpen((!_open || isTrigger) && _open)}
    >
      <DrawerTrigger
        onClick={
          isTrigger
            ? () => setOpen((_open) => !_open)
            : () => router.replace(`/${today.toString(10)}`)
        }
      >
        <Image
          priority
          src="/logo4.svg"
          layout="fixed"
          width={150}
          height={50}
          alt="logo"
        />
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="text-left">
          <DrawerTitle>Explore Tracks</DrawerTitle>
          <DrawerDescription>Recent and Featured Tracks</DrawerDescription>
        </DrawerHeader>
        <Carousel className="w-full max-w-sm m-auto mb-4 px-4">
          <CarouselContent>
            {previous.map((id, index) => (
              <CarouselItem
                key={id.toString(10)}
                className="overflow-hidden relative basis-1/3 md:basis-1/2 lg:basis-1/3 cursor-pointer"
              >
                <div>
                  <Card
                    className="aspect-[8/13] rounded-none border-0"
                    onClick={() => {
                      router.push(`/${id.toString(10)}`);
                      setOpen(false);
                    }}
                  >
                    <CardContent className="relative w-full h-full rounded-lg">
                      <StaticTrack seed={id.toString(10)} showAds ignoreTheme />
                    </CardContent>
                  </Card>
                </div>
              </CarouselItem>
            ))}
            {FEATURED_IDS.map((id, index) => (
              <CarouselItem
                key={id}
                className="overflow-hidden relative basis-1/3 md:basis-1/2 lg:basis-1/3 cursor-pointer"
              >
                <div>
                  <Card
                    className="aspect-[8/13] h-full p-0 border-0 rounded-none"
                    onClick={() => {
                      router.push(`/${id}`);
                      setOpen(false);
                    }}
                  >
                    <CardContent className="relative w-full h-full rounded-lg">
                      <StaticTrack seed={id} showAds ignoreTheme />
                    </CardContent>
                  </Card>
                </div>
              </CarouselItem>
            ))}
            <CarouselItem className="overflow-hidden  relative basis-1/3 md:basis-1/2 lg:basis-1/3">
              <div>
                <Card
                  className="aspect-[8/13] h-full p-0 border-0 rounded-none"
                  onClick={() => {
                    router.push(`/${getRandomInt(1, 102)}`);
                    setOpen(false);
                  }}
                >
                  <CardContent className="text-center flex flex-col justify-center items-center relative w-full h-full rounded-lg">
                    Random Track
                  </CardContent>
                </Card>
              </div>
            </CarouselItem>
          </CarouselContent>
        </Carousel>

        <DrawerHeader className="text-left">
          <DrawerTitle>Wallet</DrawerTitle>
          <DrawerDescription>Manage Your Account</DrawerDescription>
          {isConnected ? (
            <>
              <div className="flex flex-row items-center justify-between my-2">
                <div className="flex flex-row items-center justify-start space-x-2">
                  <Avatar>
                    <AvatarImage alt={address} src={ensData?.avatar} />
                    <AvatarFallback>
                      <Blockies seed={address} />
                    </AvatarFallback>
                  </Avatar>
                  <p
                    className="ml-4 overflow-hidden text-xl"
                    data-tooltip={address}
                  >
                    {ensData?.displayName?.length > 25 ? (
                      <Marquee>
                        <span className="mr-8">{ensData?.displayName}</span>
                      </Marquee>
                    ) : (
                      ensData?.displayName
                    )}
                  </p>
                </div>
                <div className="flex flex-col justify-center items-center">
                  <p
                    className={
                      balanceData?.value < 1600000000000000n ? "text-red" : ""
                    }
                  >
                    {parseFloat(balanceData?.formatted).toFixed(4)} ETH on Base
                  </p>
                  <a
                    target="_blank"
                    href="https://bridge.base.org/deposit"
                    className="text-xs text-blue"
                    rel="noreferrer"
                  >
                    Bridge to Base
                  </a>
                </div>
              </div>
              <div className="flex flex-row items-center justify-start my-2 space-x-5">
                {granted === "unsupported" &&
                  (prompt ? (
                    <Button
                      className="text-sm px-2 py-1 justify-center w-full"
                      onClick={() => promptToInstall()}
                    >
                      Install Speedtracer for Push Notifications
                    </Button>
                  ) : (
                    <p>
                      Save Speedtracer to your home screen for Push
                      Notifications
                    </p>
                  ))}
                {granted === "default" ? (
                  <Button
                    className="text-sm px-2 py-1 justify-center w-full"
                    onClick={() => register({ address })}
                  >
                    Get Push Notifications
                  </Button>
                ) : null}
                {granted === "granted" && address ? (
                  <PushNotifications />
                ) : null}
              </div>

              <Button
                className="text-sm px-2 py-1 justify-center"
                theme={{
                  background: "transparent",
                  crashBarrier: "red",
                }}
                onClick={() => disconnect()}
              >
                Disconnect
              </Button>
            </>
          ) : (
            <Button
              className="text-center justify-center"
              onClick={() => {
                setOpen(false);
                openConnectModal();
              }}
            >
              Connect Wallet
            </Button>
          )}
        </DrawerHeader>
        {/* <Card className="relative w-full max-w-sm m-auto"> */}

        {/* </Card> */}
        <DrawerFooter>
          <DrawerClose>Close</DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default MenuDrawer;
